import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../store'
import { getServicesManager } from '../services'
import { MS_TO_MICRO } from '../cfg/const'
import { 
  MegaPromptComposer, 
  SplitComposer, 
  VoiceBot, 
  ChatBot 
} from '../interfaces/bot-config'
import { 
  StateCollection,
  SessionWebhook 
} from '../interfaces/payloads'

interface TeamCopyOptions {
  includePrompts?: boolean
  includeVoiceBots?: boolean
  includeChatBots?: boolean
  includeStateCollections?: boolean
  includeWebhooks?: boolean
}

interface TeamCopyProgress {
  total: number
  completed: number
  currentStep: string
  isComplete: boolean
  error?: string
}

export const useTeamCopy = () => {
  const dispatch = useDispatch<AppDispatch>()
  const [isLoading, setIsLoading] = useState(false)
  const [progress, setProgress] = useState<TeamCopyProgress>({
    total: 0,
    completed: 0,
    currentStep: '',
    isComplete: false
  })

  const copyTeamConfiguration = async (
    sourceTeamId: string,
    targetTeamId: string,
    options: TeamCopyOptions = {
      includePrompts: true,
      includeVoiceBots: true,
      includeChatBots: true,
      includeStateCollections: true,
      includeWebhooks: true
    }
  ): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true)
    setProgress({
      total: 0,
      completed: 0,
      currentStep: 'Initializing...',
      isComplete: false
    })

    try {
      const trellus = getServicesManager()
      const originalTeamId = trellus.selectedTeamId

      // Calculate total steps (order matters: state collections → webhooks → prompts → bots)
      const steps = []
      if (options.includeStateCollections) steps.push('stateCollections')
      if (options.includeWebhooks) steps.push('webhooks')
      if (options.includePrompts) steps.push('prompts')
      if (options.includeVoiceBots) steps.push('voiceBots')
      if (options.includeChatBots) steps.push('chatBots')

      setProgress(prev => ({ ...prev, total: steps.length }))

      // Temporarily switch to source team to fetch data
      trellus.selectedTeamId = sourceTeamId
      
      let completedSteps = 0
      const stateIdMapping: Record<string, string> = {} // Maps old state_id to new state_id
      const composerIdMapping: Record<string, string> = {} // Maps old composer_id to new composer_id

      // Step 1: Copy State Collections (must be first)
      if (options.includeStateCollections) {
        setProgress(prev => ({ ...prev, currentStep: 'Copying state collections...', completed: completedSteps }))
        
        const stateFields = await trellus.getStateFields()
        
        trellus.selectedTeamId = targetTeamId
        
        if (stateFields?.state_collections?.length) {
          for (const collection of stateFields.state_collections) {
            // Find fields that belong to this collection
            const collectionFields = stateFields.state_fields?.filter(field => field.state_id === collection.state_id) || []
            
            const newCollection = {
              name: collection.name,
              fields: collectionFields
            }
            const result = await trellus.createStateCollection(newCollection)
            
            // Map old state_id to new state_id for split prompt substitution
            if (result?.state_id) {
              stateIdMapping[collection.state_id] = result.state_id
            }
          }
        }

        completedSteps++
        setProgress(prev => ({ ...prev, completed: completedSteps }))
        trellus.selectedTeamId = sourceTeamId
      }

      // Step 2: Copy Webhooks (must be before prompts)
      if (options.includeWebhooks) {
        setProgress(prev => ({ ...prev, currentStep: 'Copying webhooks...', completed: completedSteps }))
        
        const webhooks = await trellus.getSessionWebhooks()
        
        trellus.selectedTeamId = targetTeamId
        
        if (webhooks?.webhooks?.length) {
          for (const webhook of webhooks.webhooks) {
            // Find payloads for this webhook (registries are not copied)
            const webhookPayloads = webhooks.payloads?.filter(p => p.webhook_id === webhook.webhook_id) || []
            
            const newWebhook = {
              url: webhook.url,
              http_verb: webhook.http_verb,
              encoding: webhook.encoding,
              notify_not_ok: webhook.notify_not_ok,
              payloads: webhookPayloads
            }
            await trellus.createSessionWebhook(newWebhook)
          }
        }

        completedSteps++
        setProgress(prev => ({ ...prev, completed: completedSteps }))
        trellus.selectedTeamId = sourceTeamId
      }

      // Step 3: Copy Prompts (with proper state collection substitution)
      if (options.includePrompts) {
        setProgress(prev => ({ ...prev, currentStep: 'Copying prompts...', completed: completedSteps }))
        
        const [megaComposers, splitComposers] = await Promise.all([
          trellus.getMegaComposer(),
          trellus.getSplitComposers()
        ])

        // Switch to target team
        trellus.selectedTeamId = targetTeamId

        // Copy mega composers (simple copy)
        if (megaComposers?.length) {
          for (const composer of megaComposers) {
            const newComposer = {
              ...composer,
              composer_id: undefined, // Let the API generate new ID
              created_at: new Date().getTime() * MS_TO_MICRO // Update timestamp for new copy
            }
            const createdComposer = await trellus.addMegaComposer(newComposer as any)
            
            // Map old composer_id to new composer_id
            if (createdComposer?.composer_id) {
              composerIdMapping[composer.composer_id] = createdComposer.composer_id
            }
          }
        }

        // Copy split composers with state collection substitution
        if (splitComposers?.composers?.length) {
          for (const composer of splitComposers.composers) {
            // Find matching state collection in the new team
            const newStateId = stateIdMapping[composer.state_id] || composer.state_id
            
            // Find goals for this composer
            const composerGoals = splitComposers.goals?.filter(goal => goal.composer_id === composer.composer_id) || []
            
            const newComposer = {
              composer_name: composer.composer_name,
              state_id: newStateId, // Use mapped state_id
              created_at: Date.now() * MS_TO_MICRO, // Convert to microseconds for Python datetime
              goals: composerGoals // Include goals in creation
            }
            const createdComposer = await trellus.createSplitComposer(newComposer)
            
            // Map old composer_id to new composer_id
            if (createdComposer?.composer_id) {
              composerIdMapping[composer.composer_id] = createdComposer.composer_id
            }
          }
        }

        completedSteps++
        setProgress(prev => ({ ...prev, completed: completedSteps }))
        trellus.selectedTeamId = sourceTeamId
      }

      // Step 4: Copy Voice Bots
      if (options.includeVoiceBots) {
        setProgress(prev => ({ ...prev, currentStep: 'Copying voice bots...', completed: completedSteps }))
        
        const voiceBots = await trellus.getVoiceBots()
        
        trellus.selectedTeamId = targetTeamId
        
        if (voiceBots?.length) {
          for (const bot of voiceBots) {
            // Use mapped composer_id if available, skip if composer wasn't copied
            const newComposerId = composerIdMapping[bot.composer_id]
            if (!newComposerId) {
              console.warn(`Skipping voice bot ${bot.agent_name} - composer not found in mapping`)
              continue
            }
            
            const newBot = {
              ...bot,
              agent_id: undefined, // Let the API generate new ID
              composer_id: newComposerId // Use mapped composer_id
            }
            await trellus.addVoiceBot(newBot)
          }
        }

        completedSteps++
        setProgress(prev => ({ ...prev, completed: completedSteps }))
        trellus.selectedTeamId = sourceTeamId
      }

      // Step 5: Copy Chat Bots
      if (options.includeChatBots) {
        setProgress(prev => ({ ...prev, currentStep: 'Copying chat bots...', completed: completedSteps }))
        
        const chatBots = await trellus.getChatBots()
        
        trellus.selectedTeamId = targetTeamId
        
        if (chatBots?.length) {
          for (const bot of chatBots) {
            // Use mapped composer_id if available, skip if composer wasn't copied
            const newComposerId = composerIdMapping[bot.composer_id]
            if (!newComposerId) {
              console.warn(`Skipping chat bot ${bot.agent_name} - composer not found in mapping`)
              continue
            }
            
            const newBot = {
              ...bot,
              agent_id: undefined, // Let the API generate new ID
              composer_id: newComposerId // Use mapped composer_id
            }
            await trellus.addChatBot(newBot)
          }
        }

        completedSteps++
        setProgress(prev => ({ ...prev, completed: completedSteps }))
      }

      // Restore original team selection
      trellus.selectedTeamId = originalTeamId

      setProgress(prev => ({ 
        ...prev, 
        currentStep: 'Copy complete!', 
        completed: completedSteps, 
        isComplete: true 
      }))

      return { success: true }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
      setProgress(prev => ({ 
        ...prev, 
        error: errorMessage, 
        isComplete: true 
      }))
      
      return { success: false, error: errorMessage }
    } finally {
      setIsLoading(false)
    }
  }

  const resetProgress = () => {
    setProgress({
      total: 0,
      completed: 0,
      currentStep: '',
      isComplete: false
    })
  }

  return {
    copyTeamConfiguration,
    isLoading,
    progress,
    resetProgress
  }
}